import * as THREE from 'three';

export class Engine {
  public scene: THREE.Scene;
  public renderer: THREE.WebGLRenderer;
  public camera: THREE.PerspectiveCamera;
  public canvas: HTMLCanvasElement;
  public clock: THREE.Clock;

  public sunLight!: THREE.DirectionalLight;
  public ambientLight!: THREE.AmbientLight;
  public hemiLight!: THREE.HemisphereLight;

  private onResizeCallbacks: Array<() => void> = [];

  constructor(canvasId: string = 'game-canvas') {
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) {
      throw new Error(`Canvas with id ${canvasId} not found`);
    }
    this.canvas = canvas;

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0xbfdbfe);
    // Crisp atmospheric depth: objects under 190m are crystal clear with subtle natural horizon haze
    this.scene.fog = new THREE.Fog(0xdbeafe, 190, 650);

    this.camera = new THREE.PerspectiveCamera(
      62,                                   // 62° vertical FOV — authentic Degener SimDrive scale (~94° HFOV) framing header mirror, left pillar & cluster
      window.innerWidth / window.innerHeight,
      0.08,                                 // Near plane 8cm — avoids clipping steering wheel
      1200
    );
    this.camera.position.set(0, 10, 20);

    this.renderer = new THREE.WebGLRenderer({
      canvas: this.canvas,
      antialias: true,
      powerPreference: 'high-performance',
      alpha: false,
    });
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.15;

    this.clock = new THREE.Clock();

    this.setupSkyDome();
    this.setupLighting();
    this.setupResizeHandler();
  }

  private skyDome!: THREE.Mesh;
  private skyUniforms!: Record<string, THREE.IUniform>;

  private setupSkyDome() {
    const vertexShader = `
      varying vec3 vWorldPosition;
      void main() {
        vec4 worldPosition = modelMatrix * vec4(position, 1.0);
        vWorldPosition = worldPosition.xyz;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
      }
    `;

    const fragmentShader = `
      uniform vec3 topColor;
      uniform vec3 bottomColor;
      uniform vec3 sunPosition;
      uniform float offset;
      uniform float exponent;
      varying vec3 vWorldPosition;

      void main() {
        float h = normalize(vWorldPosition + offset).y;
        vec3 sky = mix(bottomColor, topColor, max(pow(max(h, 0.0), exponent), 0.0));
        
        vec3 dir = normalize(vWorldPosition);
        vec3 sunDir = normalize(sunPosition);
        float sunDot = max(dot(dir, sunDir), 0.0);
        vec3 sunGlow = vec3(1.0, 0.96, 0.85) * pow(sunDot, 14.0) * 0.40;
        vec3 sunDisc = vec3(1.0, 1.0, 0.98) * pow(sunDot, 320.0) * 1.6;

        gl_FragColor = vec4(sky + sunGlow + sunDisc, 1.0);
      }
    `;

    this.skyUniforms = {
      topColor: { value: new THREE.Color(0x2563eb) },    // Clear bright summer blue
      bottomColor: { value: new THREE.Color(0xe0f2fe) }, // Soft horizon atmospheric haze
      sunPosition: { value: new THREE.Vector3(70, 100, 50) },
      offset: { value: 30 },
      exponent: { value: 0.60 },
    };

    const skyMat = new THREE.ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: this.skyUniforms,
      side: THREE.BackSide,
      depthWrite: false,
    });

    const skyGeo = new THREE.SphereGeometry(800, 32, 24);
    this.skyDome = new THREE.Mesh(skyGeo, skyMat);
    this.scene.add(this.skyDome);
  }

  private setupLighting() {
    // High-visibility natural ambient light to illuminate cabin & in-shadow details
    this.ambientLight = new THREE.AmbientLight(0xffffff, 0.78);
    this.scene.add(this.ambientLight);

    // Sky/Ground hemisphere light for rich contrast and depth
    this.hemiLight = new THREE.HemisphereLight(0xe0f2fe, 0x475569, 0.70);
    this.hemiLight.position.set(0, 80, 0);
    this.scene.add(this.hemiLight);

    // Crisp directional sunlight with sharp shadow map
    this.sunLight = new THREE.DirectionalLight(0xfffaed, 1.65);
    this.sunLight.position.set(70, 100, 50);
    this.sunLight.castShadow = true;
    this.sunLight.shadow.mapSize.width = 2048;
    this.sunLight.shadow.mapSize.height = 2048;
    this.sunLight.shadow.camera.near = 0.5;
    this.sunLight.shadow.camera.far = 350;

    const d = 85;
    this.sunLight.shadow.camera.left = -d;
    this.sunLight.shadow.camera.right = d;
    this.sunLight.shadow.camera.top = d;
    this.sunLight.shadow.camera.bottom = -d;
    this.sunLight.shadow.bias = -0.0002;

    this.scene.add(this.sunLight);
    this.scene.add(this.sunLight.target);
  }

  public updateCameraFov(width: number, height: number) {
    const aspect = width / height;
    this.camera.aspect = aspect;

    // Aspect-aware FOV: On mobile portrait (aspect ~0.56) or narrow viewports (< 1.35),
    // calculate vertical FOV to preserve a comfortable ~88° horizontal FOV,
    // avoiding tunnel vision and keeping dashboard dials and road in view.
    if (aspect < 1.35) {
      const targetHFOV = 88 * (Math.PI / 180);
      const vFovRad = 2 * Math.atan(Math.tan(targetHFOV / 2) / aspect);
      this.camera.fov = Math.min(96, Math.max(76, vFovRad * (180 / Math.PI)));
    } else {
      this.camera.fov = 62; // Authentic Degener SimDrive scale (~94° HFOV) framing header mirror, left pillar & cluster
    }
    this.camera.updateProjectionMatrix();
  }

  private setupResizeHandler() {
    this.updateCameraFov(window.innerWidth, window.innerHeight);

    window.addEventListener('resize', () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      this.updateCameraFov(width, height);

      this.renderer.setSize(width, height);
      this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));

      for (const cb of this.onResizeCallbacks) {
        cb();
      }
    });
  }

  public onResize(callback: () => void) {
    this.onResizeCallbacks.push(callback);
  }

  public updateSunPosition(targetPosition: THREE.Vector3) {
    if (this.skyDome) {
      this.skyDome.position.set(targetPosition.x, targetPosition.y - 20, targetPosition.z);
    }
    this.sunLight.position.set(
      targetPosition.x + 60,
      targetPosition.y + 90,
      targetPosition.z + 40
    );
    this.sunLight.target.position.copy(targetPosition);
    this.sunLight.target.updateMatrixWorld();
  }

  public render() {
    this.renderer.render(this.scene, this.camera);
  }
}
